import axios, { AxiosInstance } from "axios";
import { partialRight } from "lodash";
import { stringify } from "qs";
import {
  GetInput,
  PostInput,
  DeleteInput,
  PatchInput,
  PutInput,
} from "./ApiClientUtils";
import { withAuthentication } from "./AuthUtils";

export interface ApiClientConfig {
  baseURL: string;
  headers?: Record<string, string>;
}

export class ApiClient {
  private axios: AxiosInstance;

  constructor({ baseURL, headers }: ApiClientConfig) {
    this.axios = axios.create({
      baseURL,
      headers,
      paramsSerializer: partialRight(stringify, { arrayFormat: "repeat" }),
    });
  }

  async get<ReturnType, ParamsType = undefined>({
    url,
    params,
    authentication,
    responseType,
  }: GetInput<ParamsType>): Promise<ReturnType> {
    const config = {
      url,
      params,
      responseType,
    };

    const response = await this.axios.request<ReturnType>(
      await withAuthentication({ authentication })(config)
    );

    return response.data;
  }

  async patch<ReturnType, ParamsType = undefined, BodyType = undefined>({
    url,
    body: data,
    params,
    authentication,
  }: PatchInput<ParamsType, BodyType>): Promise<ReturnType> {
    const config = {
      method: "PATCH" as const,
      url,
      params,
      data,
    };

    const response = await this.axios.request<ReturnType>(
      await withAuthentication({ authentication })(config)
    );

    return response.data;
  }

  async post<ReturnType, ParamsType = undefined, BodyType = undefined>({
    url,
    body: data,
    params,
    authentication,
  }: PostInput<ParamsType, BodyType>): Promise<ReturnType> {
    const config = {
      method: "POST" as const,
      url,
      params,
      data,
    };

    const response = await this.axios.request<ReturnType>(
      await withAuthentication({ authentication })(config)
    );

    return response.data;
  }

  async delete<ReturnType, ParamsType = undefined, BodyType = undefined>({
    url,
    params,
    body: data,
    authentication,
  }: DeleteInput<ParamsType, BodyType>): Promise<ReturnType> {
    const config = {
      method: "DELETE" as const,
      url,
      data,
      params,
    };

    const response = await this.axios.request<ReturnType>(
      await withAuthentication({ authentication })(config)
    );

    return response.data;
  }

  async put<ReturnType, ParamsType = undefined, BodyType = undefined>({
    url,
    body: data,
    params,
    authentication,
  }: PutInput<ParamsType, BodyType>): Promise<ReturnType> {
    const config = {
      method: "PUT" as const,
      url,
      data,
      params,
    };

    const response = await this.axios.request<ReturnType>(
      await withAuthentication({ authentication })(config)
    );

    return response.data;
  }
}
